
import { HermandadesClient } from "@/components/hermandades/HermandadesClient";
import { useAuth } from "@/hooks/useAuth";
import { useState, useEffect, useMemo } from "react";
import { JoinRequest, SocialPost, SocialStory, Contract, DiaSS } from "@/lib/types";
import { collection, getDocs, query, where, orderBy, limit } from "firebase/firestore";
import { db } from "@/firebase/config";
import { sanitizeFirestoreData } from "@/lib/utils";
import { useCollection } from "@/firebase/firestore/use-collection";
import { getDiasSS } from "@/lib/actions";

export default function JartinetHermandadesPage() {
    const { user, userData, loading: authLoading, jartinetData, handleActionCompletion } = useAuth();
    const [initialRequests, setInitialRequests] = useState<Record<string, JoinRequest[]>>({});
    // Notificaciones por hermandad: solicitudes pendientes + contratos recibidos pendientes
    const [notificationsByHermandad, setNotificationsByHermandad] = useState<Record<string, number>>({});
    // Programa de Semana Santa — de ahí sale el día de salida de cada hermandad.
    // `null` = todavía cargando (la lista se agrupa provisionalmente por el campo `dia`).
    const [diasSS, setDiasSS] = useState<DiaSS[] | null>(null);

    // Carga en paralelo, sin bloquear el render de la página
    useEffect(() => {
        getDiasSS()
            .then(res => setDiasSS(res.dias || []))
            .catch(() => setDiasSS([]));
    }, []);

    // Posts recientes (para las vistas de detalle, que filtran por authorId en memoria).
    // Antes leía la colección ENTERA en cada visita; ahora los 250 más recientes ([158]).
    // El historial completo de una entidad sigue en su ficha pública (/hermandades/:id).
    const postsQuery = useMemo(
        () => query(collection(db, 'social_posts'), orderBy('createdAt', 'desc'), limit(250)),
        []
    );
    const { data: rawPosts } = useCollection<SocialPost>(postsQuery);

    // Real-time subscription: active stories only
    const storiesQuery = useMemo(() => {
        const now = new Date();
        return query(collection(db, 'social_stories'), where('expiresAt', '>', now));
    }, []);
    const { data: rawStories } = useCollection<SocialStory>(storiesQuery);

    // Sanitize and memoize posts
    const allPosts = useMemo(
        () => (rawPosts || []).map(p => sanitizeFirestoreData(p) as SocialPost),
        [rawPosts]
    );

    // Sanitize stories and group by author key
    const storiesByAuthor = useMemo(() => {
        return (rawStories || []).reduce((acc, story) => {
            const sanitized = sanitizeFirestoreData(story) as SocialStory;
            const key = `${sanitized.authorType}:${sanitized.authorId}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(sanitized);
            return acc;
        }, {} as Record<string, SocialStory[]>);
    }, [rawStories]);

    // Fetch join requests without blocking render (admin/HermanoMayor only)
    useEffect(() => {
        if (!user || !jartinetData.hermandades?.length) return;

        const isUserAdmin = userData?.role === 'admin';
        const hermandadesToFetch = isUserAdmin
            ? jartinetData.hermandades
            : jartinetData.hermandades.filter(h => Array.isArray(h.hermanosMayores) && h.hermanosMayores.includes(user.uid));

        if (hermandadesToFetch.length === 0) return;

        (async () => {
            const requests: Record<string, JoinRequest[]> = {};
            for (const h of hermandadesToFetch) {
                try {
                    const snap = await getDocs(query(collection(db, `jartinet_hermandades/${h.id}/requests`)));
                    requests[h.id] = snap.docs.map(doc =>
                        sanitizeFirestoreData({ ...doc.data(), id: doc.id }) as JoinRequest
                    );
                } catch {
                    requests[h.id] = [];
                }
            }
            setInitialRequests(requests);

            // Recuento de notificaciones por hermandad (solicitudes + contratos pendientes)
            const counts: Record<string, number> = {};
            for (const h of hermandadesToFetch) {
                const pendingReqs = (requests[h.id] || []).filter(r => (r.status ?? 'pending') === 'pending').length;
                counts[h.id] = pendingReqs;
            }

            // Contratos recibidos pendientes — en bloques de 30 por el límite de `in`
            const recipientIds = hermandadesToFetch.map(h => `hermandad:${h.id}`);
            const chunk = <T,>(arr: T[], size: number): T[][] => {
                const out: T[][] = [];
                for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
                return out;
            };
            try {
                for (const group of chunk(recipientIds, 30)) {
                    const cSnap = await getDocs(query(
                        collection(db, 'contracts'),
                        where('recipientId', 'in', group),
                        where('status', '==', 'pending')
                    ));
                    cSnap.docs.forEach(d => {
                        const c = sanitizeFirestoreData({ ...d.data(), id: d.id }) as Contract;
                        const hid = c.recipientId.split(':')[1];
                        if (hid) counts[hid] = (counts[hid] || 0) + 1;
                    });
                }
            } catch { /* contratos no críticos para el badge */ }

            setNotificationsByHermandad(counts);
        })();
    }, [user, userData?.role, jartinetData.hermandades]);

    if (authLoading) return null;

    return (
        <HermandadesClient
            initialHermandades={jartinetData.hermandades || []}
            initialUserProfiles={jartinetData.userProfiles || {}}
            initialRequests={initialRequests}
            notifications={notificationsByHermandad}
            initialStories={storiesByAuthor}
            diasSS={diasSS}
            allPosts={allPosts}
            allOrganizaciones={jartinetData.organizaciones || []}
            allBandas={jartinetData.bandas || []}
            currentUser={user ? { uid: user.uid } : null}
            onAction={handleActionCompletion}
        />
    );
}
